import React, { useEffect, useState } from 'react';
import { Loader2, Save, Send, MessageSquareWarning, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { sellersAPI } from '../../services/api';
import { SellerStatusBadge, SellerEmptyHero } from './SellerStatus';
import SellerApplicationForm from './SellerApplicationForm';
import SellerDocuments from './SellerDocuments';

const MISSING_LABELS = { company_name: 'raison sociale', siret: 'SIRET', activity_type: 'type d\u2019activité', legal_form: 'forme juridique', address: 'adresse', contact: 'e-mail et téléphone', representative: 'représentant légal', accepted_terms: 'attestation' };

const pick = (s) => ({ company_name: s.company_name, trade_name: s.trade_name, legal_form: s.legal_form, siret: s.siret, vat_number: s.vat_number, activity_type: s.activity_type, activity_description: s.activity_description, address: s.address, contact: s.contact, representative: s.representative, accepted_terms: s.accepted_terms });

const StatusMessage = ({ seller }) => {
  if (seller.status === 'pending' || seller.status === 'under_review') return <p className="text-sm text-gray-700" data-testid="seller-msg-pending">Votre dossier est en cours d&rsquo;examen par notre équipe. Vous serez informé par e-mail de la décision ou d&rsquo;une demande de complément.</p>;
  if (seller.status === 'approved') return <p className="text-sm text-green-800" data-testid="seller-msg-approved">Votre compte vendeur est approuvé. Les fonctionnalités de vente vous seront ouvertes lors du lancement de la marketplace.</p>;
  if (seller.status === 'rejected') return <p className="text-sm text-red-800" data-testid="seller-msg-rejected"><strong>Candidature refusée.</strong> Motif : {seller.rejection_reason || '—'}. Contactez-nous pour toute question.</p>;
  if (seller.status === 'suspended') return <p className="text-sm text-red-900" data-testid="seller-msg-suspended"><strong>Compte suspendu.</strong> Motif : {seller.suspension_reason || '—'}. Les fonctions vendeur sont désactivées jusqu&rsquo;à régularisation.</p>;
  return null;
};

// Espace « Devenir vendeur » : candidature, brouillon, justificatifs, soumission, statut
const SellerApplicationSection = () => {
  const [seller, setSeller] = useState(undefined);
  const [meta, setMeta] = useState({ types: [], doc_kinds: {} });
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const apply = (s) => { setSeller(s); if (s) setForm(pick(s)); };
  useEffect(() => {
    Promise.all([sellersAPI.me(), sellersAPI.activityTypes()]).then(([m, t]) => { apply(m.seller); setMeta(t); }).catch(() => apply(null));
  }, []);

  const start = async () => { setBusy(true); try { apply((await sellersAPI.create({})).seller); } catch (e) { toast.error(e.response?.data?.detail || 'Création impossible'); } finally { setBusy(false); } };
  const save = async () => {
    setBusy(true);
    try { apply((await sellersAPI.update(form)).seller); toast.success('Brouillon enregistré'); return true; }
    catch (e) { const d = e.response?.data?.detail; toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(', ') : d || 'Enregistrement impossible'); return false; }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!(await save())) return;
    setBusy(true);
    try { apply((await sellersAPI.submit()).seller); toast.success('Candidature soumise'); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.missing ? `Dossier incomplet : ${d.missing.map((m) => MISSING_LABELS[m] || m.replace('document:', 'justificatif ')).join(', ')}` : d || 'Soumission impossible'); }
    finally { setBusy(false); }
  };

  if (seller === undefined) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-orange-500" /></div>;
  if (seller === null) return <SellerEmptyHero onStart={start} busy={busy} />;
  const editable = seller.status === 'draft';
  const missing = seller.missing || [];

  return (
    <div className="space-y-5" data-testid="seller-application">
      <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500">Statut de la candidature</p>
          <div className="mt-1"><SellerStatusBadge status={seller.status} size="lg" /></div>
        </div>
        <div className="text-xs text-gray-500 text-right">
          {seller.submitted_at && <p>Soumise le {new Date(seller.submitted_at).toLocaleDateString('fr-FR')}</p>}
          {seller.decided_at && <p>Décision le {new Date(seller.decided_at).toLocaleDateString('fr-FR')}</p>}
        </div>
      </div>
      <StatusMessage seller={seller} />
      {seller.open_info_request && (
        <div className="rounded-xl border border-orange-300 bg-orange-50 p-4 flex gap-3" data-testid="seller-info-request">
          <MessageSquareWarning className="text-orange-600 shrink-0" size={20} />
          <div>
            <p className="font-semibold text-orange-900 text-sm">Complément demandé par OTOPIECES</p>
            <p className="text-sm text-orange-900/90 mt-1 whitespace-pre-line">{seller.open_info_request.message}</p>
            <p className="text-xs text-orange-800 mt-2">Complétez votre dossier ci-dessous puis soumettez-le à nouveau.</p>
          </div>
        </div>
      )}
      <SellerApplicationForm form={form} setForm={setForm} types={meta.types} disabled={!editable} />
      <SellerDocuments seller={seller} docKinds={meta.doc_kinds} disabled={!editable} onChange={apply} />
      {editable && (
        <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-xs text-gray-500" data-testid="seller-missing">{missing.length ? `À compléter : ${missing.map((m) => MISSING_LABELS[m] || m.replace('document:', 'justificatif ')).join(', ')}` : 'Dossier complet, prêt à être soumis.'}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={save} disabled={busy} data-testid="seller-save-btn"><Save size={15} className="mr-2" /> Enregistrer le brouillon</Button>
            <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="seller-submit-btn"><Send size={15} className="mr-2" /> Soumettre</Button>
          </div>
        </div>
      )}
      {!!seller.history?.length && (
        <details className="bg-white rounded-xl shadow-sm p-5" data-testid="seller-history">
          <summary className="text-sm font-semibold text-gray-800 cursor-pointer flex items-center gap-2"><History size={15} /> Historique du dossier</summary>
          <ul className="mt-3 space-y-1.5 text-xs text-gray-600">
            {[...seller.history].reverse().map((h, i) => <li key={i}>{new Date(h.at).toLocaleString('fr-FR')} — {h.action}{h.to_status ? ` → ${h.to_status}` : ''}{h.note && h.by_role === 'admin' ? ` : ${h.note}` : ''}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
};

export default SellerApplicationSection;
